import { useMemo, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import Prism from 'prismjs';
import { InfiosLink } from '@infios/uikit/react';
import { isReferenceHref, parseReference } from '../../services/reference';
import { useReferenceAction } from '../../context/ReferenceActionContext';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-yaml';
import styles from './Markdown.module.css';

/**
 * Sanitised markdown renderer. remark-gfm adds tables/task-lists/strikethrough;
 * rehype-sanitize strips any raw HTML/script the model emits (XSS defence-in-
 * depth — the content comes from an LLM, so it is never trusted). Links are
 * forced to open in a new tab with noopener. Code blocks are syntax-highlighted
 * with Prism; the highlighted output is produced from escaped code text, so it
 * does not reintroduce an injection vector.
 */

// Allow the className Prism/remark put on <code>/<pre> through the sanitizer,
// and permit the custom `archer-ref:` link scheme (otherwise sanitize would
// strip the href before our link renderer ever sees it).
const schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code ?? []), ['className']],
    span: [...(defaultSchema.attributes?.span ?? []), ['className']],
    a: [...(defaultSchema.attributes?.a ?? []), ['target'], ['rel']],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), 'archer-ref'],
  },
};

/** Extracts plain text from a link's children for use as the reference label. */
function nodeText(children: ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(nodeText).join('');
  return '';
}

/**
 * Renders an anchor. `archer-ref:` links become action chips that ask the host
 * to navigate (via a postMessage) rather than following an href — so the same
 * agent reply works wherever the iframe is embedded. All other links open
 * externally with noopener.
 */
function MarkdownLink({
  node: _node,
  href,
  children,
  onCopy: _onCopy,
  onCopyCapture: _onCopyCapture,
  ...props
}: ComponentPropsWithoutRef<'a'> & { node?: unknown }) {
  const onReference = useReferenceAction();

  if (isReferenceHref(href)) {
    const reference = parseReference(href as string, nodeText(children));
    if (reference) {
      return (
        <InfiosLink
          className={styles.refLink}
          data-ref-type={reference.type}
          showIcon
          onClick={() => onReference?.(reference)}
        >
          {children}
        </InfiosLink>
      );
    }
  }

  return (
    // Spreading raw anchor props onto InfiosLink trips up on event handler
    // signatures typed for HTMLAnchorElement vs the web component's own
    // element type (e.g. onCut) — widen to unknown at the spread site since
    // the props themselves are still just plain anchor attributes/handlers.
    <InfiosLink {...(props as Record<string, unknown>)} href={href} target="_blank" showIcon>
      {children}
    </InfiosLink>
  );
}

function detectLanguage(className?: string): string | null {
  const match = /language-(\w+)/.exec(className ?? '');
  return match ? match[1] : null;
}

function CodeBlock({ className, children, ...rest }: ComponentPropsWithoutRef<'code'>) {
  const raw = String(children ?? '');
  const lang = detectLanguage(className);
  const isInline = !className && !raw.includes('\n');

  const html = useMemo(() => {
    if (isInline || !lang) return null;
    const grammar = Prism.languages[lang];
    if (!grammar) return null;
    return Prism.highlight(raw.replace(/\n$/, ''), grammar, lang);
  }, [raw, lang, isInline]);

  if (isInline) {
    return (
      <code className={styles.inlineCode} {...rest}>
        {children}
      </code>
    );
  }

  if (html) {
    return (
      <code
        className={`${styles.code} language-${lang}`}
        // Prism output is built from escaped code text — safe.
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <code className={styles.code} {...rest}>
      {children}
    </code>
  );
}

interface MarkdownProps {
  content: string;
}

export function Markdown({ content }: MarkdownProps) {
  return (
    <div className={styles.markdown}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, schema]]}
        components={{
          a: MarkdownLink,
          code: CodeBlock,
          pre: ({ node: _node, ...props }) => <pre className={styles.pre} {...props} />,
          table: ({ node: _node, ...props }) => (
            <div className={styles.tableWrap}>
              <table {...props} />
            </div>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
